import { AlertCircle, RotateCw } from 'lucide-react';
import { FC, memo, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Typography, useTheme } from '@mui/material';

import Autocomplete from 'components/autocomplete';
import ListRow from 'components/list/list-row';
import ListCell from 'components/list/list-cell';
import List, { HeadCell } from 'components/list/list';

import { FishGroupSummary } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-result.type';
import ApplyFeedsDrawerForm from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-result-tab/fish-groups/apply-feed-drawer-form';

import { selectFishGroupsByProgram } from 'views/bluviu-nutrition/features/scenarios-v2/utils/fish-group-utils';
import { useScenarioResultState } from 'views/bluviu-nutrition/features/scenarios-v2/context/scenario-result-context';
import { ScenarioHarvestTargetType } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-cage-data.type';
import ScenarioFishGroupRowBody from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-result-tab/fish-groups/scenario-fish-group-row-body';
import { usePostProcessMapper } from 'views/bluviu-nutrition/features/scenarios-v2/hooks/use-post-process-mapper';
import { ScenarioFishGroupsProvider, useScenarioFishGroupsState } from 'views/bluviu-nutrition/features/scenarios-v2/context/scenario-fish-groups-context';

type Props = {};

const ScenarioFishGroupListComponent: FC<Props> = ({ }) => {
  const theme = useTheme();

  const {
    editedFishGroups,
    setEditedFishGroups,
    overrideFeedProgram,
    setOverrideFeedProgram,
    selectedDefaultProgramName,
    setSelectedDefaultProgramName,
    updateScenarioPostProcessQuery,
    getScenarioPostProcessQuery: { data: postProcessData, isFetching },
  } = useScenarioResultState();
  const { resetToSiteLevel } = usePostProcessMapper();

  const { setCageFeedPrograms } = useScenarioFishGroupsState();

  const { fishGroupSummaries, siteSummary, scenarioDefaults } = postProcessData ?? {};

  const selectedDefaultProgram = useMemo(
    () => siteSummary?.find((o) => o.feedPlan === selectedDefaultProgramName) ?? null,
    [siteSummary, selectedDefaultProgramName]
  );

  const [rows, setRows] = useState<FishGroupSummary[]>([]);

  const columns: HeadCell[] = [
    {
      id: 'fishGroupName',
      align: 'left',
      label: 'Fish group',
      disableSort: true,
    },
    {
      id: 'referenceProgram',
      align: 'left',
      label: 'Default prog.',
    },
    {
      id: 'overriddenProg',
      align: 'left',
      label: 'Overridden prog.',
      disableSort: true,
    },
    {
      id: 'harvestTargetWeeks',
      align: 'left',
      label: 'Harvest target',
      disableSort: true,
    },
    {
      id: 'mortalityProfile',
      align: 'left',
      label: 'Mortality',
      disableSort: true,
    },
  ];

  const resetAllInputs = () => {
    const body = resetToSiteLevel(fishGroupSummaries, scenarioDefaults);
    if (!body) return;
    updateScenarioPostProcessQuery.mutate(body, {
      onSuccess: () => {
        setEditedFishGroups(new Set());
        setOverrideFeedProgram({});
      },
    });
  };

  useEffect(() => {
    if (siteSummary?.length === 1 && !selectedDefaultProgramName) {
      setSelectedDefaultProgramName(siteSummary[0].feedPlan);
    }
  }, [siteSummary]);

  useEffect(() => {
    setRows(selectFishGroupsByProgram(fishGroupSummaries ?? [], selectedDefaultProgramName));
  }, [fishGroupSummaries, selectedDefaultProgramName]);

  useEffect(() => {
    if (!selectedDefaultProgramName) {
      setCageFeedPrograms([]);
      return;
    }

    // Find the default program to get its ID
    const defaultProgram = siteSummary?.find((o) => o.feedPlan === selectedDefaultProgramName);
    if (!defaultProgram) return;

    // Build cageFeedPrograms combining default and overrides
    const programs = rows.map((row) => {
      const overrideProgram = overrideFeedProgram[row.fishGroupId];

      if (overrideProgram) {
        const overrideProgramObj = siteSummary?.find((o) => o.feedPlan === overrideProgram.program);

        return {
          fishGroupId: row.fishGroupId,
          feedProgramId: String(overrideProgramObj?.feedPlanId),
          feedProgramName: String(overrideProgramObj?.feedPlan),
        };
      }

      return {
        fishGroupId: row.fishGroupId,
        feedProgramId: String(defaultProgram?.feedPlanId),
        feedProgramName: String(defaultProgram?.feedPlan),
      };
    });

    setCageFeedPrograms(programs);
  }, [selectedDefaultProgramName, overrideFeedProgram, rows, siteSummary, setCageFeedPrograms]);

  return (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      <Box display="flex" alignItems="center" justifyContent={'space-between'} gap={1}>
        <Box display="flex" gap={2}>
          <Autocomplete
            label="Default program"
            options={siteSummary ?? []}
            value={selectedDefaultProgram}
            disableCloseOnSelect={false}
            getOptionLabel={(option) => option.feedPlan}
            onChange={(_, value) => {
              setSelectedDefaultProgramName(value?.feedPlan ?? '');
              if (!value) setOverrideFeedProgram({});
            }}
          />
        </Box>
        <ApplyFeedsDrawerForm />
      </Box>
      {!selectedDefaultProgramName && (
        <Box display="flex" alignItems="center" gap={1}>
          <AlertCircle size={18} color={theme.palette.common.white} />
          <Typography color="white">A default program is required to compare and customize per fish group.</Typography>
        </Box>
      )}
      <Box display="flex" alignItems="center" justifyContent="end">
        {editedFishGroups.size >= 1 && (
          <IconButton
            onClick={resetAllInputs}
            disableRipple
            disabled={isFetching || updateScenarioPostProcessQuery.isPending}
            sx={{ display: 'flex', gap: 2 }}>
            <RotateCw size={16} color="white" />
            <Typography color={'white'}>Reset inputs to site level</Typography>
          </IconButton>
        )}
      </Box>
      <List
        rows={rows}
        options={columns}
        sort={setRows}
        orderBy={'referenceProgram'}
        noResult={{
          show: !rows?.length,
          text: 'No Results',
        }}>
        {rows.map((item) => {
          return (
            <ListRow
              sx={{ height: 50 }}
              key={`${item.fishGroupId}-${!!selectedDefaultProgramName}`}
              body={
                selectedDefaultProgramName
                  ? { span: columns.length + 1, options: [], children: <ScenarioFishGroupRowBody item={item} /> }
                  : undefined
              }>
              <ListCell align="left">
                <Typography variant="body2" noWrap fontWeight={'500'}>
                  {item.fishgroupName}
                </Typography>
              </ListCell>
              <ListCell align="left">
                <Typography
                  variant="body2"
                  noWrap
                  color={overrideFeedProgram[item.fishGroupId]?.program ? 'text.secondary' : 'inherit'}>
                  {selectedDefaultProgramName || '-'}
                </Typography>
              </ListCell>
              <ListCell align="left">
                <Typography variant="body2">{overrideFeedProgram[item.fishGroupId]?.program || '-'}</Typography>
              </ListCell>
              <ListCell align="left">
                <Typography variant="body2">
                  {item.harvestTarget} {item.harvestTargetType === ScenarioHarvestTargetType.WEEK.toLowerCase() ? 'weeks' : 'g'}
                </Typography>
              </ListCell>
              <ListCell align="left">
                <Typography variant="body2">{item.mortalityProfileName}</Typography>
              </ListCell>
            </ListRow>
          );
        })}
      </List>
    </Box>
  );
};

const ScenarioFishGroupList = () => {
  return (
    <ScenarioFishGroupsProvider>
      <ScenarioFishGroupListComponent />
    </ScenarioFishGroupsProvider>
  );
}

export default memo(ScenarioFishGroupList);
